import React, { useState, useEffect } from 'react';
import { ChefHat, Scale, Settings, Sparkles } from 'lucide-react';
import RecipeForm from './components/RecipeForm';
import Recipe from './components/Recipe';
import Header from './components/Header';
import { MeasurementSystem, Recipe as RecipeType } from './types';
import { generateRecipe } from './utils/api';

function App() {
  const [recipe, setRecipe] = useState<RecipeType | null>(null);
  const [measurementSystem, setMeasurementSystem] = useState<MeasurementSystem>('metric');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    function sendHeightToParent() {
      const height = document.body.scrollHeight;
      window.parent.postMessage({ type: 'resizeIframe', height }, '*');
    }

    // Send initial height
    sendHeightToParent();

    // Set up observer for content changes
    const observer = new MutationObserver(sendHeightToParent);
    observer.observe(document.body, { 
      childList: true, 
      subtree: true,
      attributes: true,
      characterData: true 
    });

    // Clean up observer
    return () => observer.disconnect();
  }, []);

  // Update iframe height when recipe changes
  useEffect(() => {
    if (recipe) {
      const height = document.body.scrollHeight;
      window.parent.postMessage({ type: 'resizeIframe', height }, '*');
    }
  }, [recipe]);

  const handleGenerateRecipe = async (ingredients: string[]) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const cleanIngredients = ingredients
        .map(ing => ing.trim())
        .filter(Boolean);
        
      if (cleanIngredients.length === 0) {
        throw new Error('Please provide at least one ingredient');
      }

      const newRecipe = await generateRecipe(cleanIngredients);
      setRecipe(newRecipe);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate recipe';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setRecipe(null);
    setError(null);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F7F4F6' }}>
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-8">
            <div className="flex items-center gap-2 mb-6">
              <ChefHat className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-semibold text-gray-800">What's in Your Kitchen?</h2>
            </div>
            
            <div className="mb-6">
              <div className="flex items-center gap-4 mb-4">
                <Scale className="w-5 h-5 text-gray-600" />
                <select
                  value={measurementSystem}
                  onChange={(e) => setMeasurementSystem(e.target.value as MeasurementSystem)}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary p-2.5"
                >
                  <option value="metric">Metric</option>
                  <option value="imperial">Imperial</option>
                </select>
              </div>
              
              <RecipeForm onSubmit={handleGenerateRecipe} isLoading={isLoading} />
              
              {error && (
                <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-lg">
                  {error}
                </div>
              )}
            </div>
          </div>

          {recipe && (
            <>
              <Recipe recipe={recipe} measurementSystem={measurementSystem} />
              <div className="mt-8 flex justify-center">
                <button
                  onClick={handleReset}
                  className="px-6 py-3 bg-secondary hover:bg-secondary-dark text-primary font-semibold rounded-lg transition-colors"
                >
                  Try New Ingredients
                </button>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default App;